import React, { useState, useEffect } from "react";
import { Calendar as CalendarIcon, Clock } from 'lucide-react';
import { staffService } from '../../../services/staffService';
import { wellnessService } from '../../../services/wellnessService';

const DateTimeSelector = ({ bookingData, onDataUpdate }) => {
  const [selectedDate, setSelectedDate] = useState(bookingData?.dateTime?.date || null);
  const [selectedSlot, setSelectedSlot] = useState(bookingData?.dateTime?.timeSlot || null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Generate next 14 days
  const generateAvailableDates = () => {
    const dates = [];
    const today = new Date();
    for (let i = 0; i < 14; i++) {
      const date = new Date(today);
      date?.setDate(today?.getDate() + i);
      dates?.push(date);
    }
    return dates;
  };

  const availableDates = generateAvailableDates();

  // RE-ENABLED - Load available slots when date/gender/services change (SAFE - has proper conditions)
  useEffect(() => {
    if (selectedDate && bookingData?.details?.gender && bookingData?.services?.length > 0) {
      loadAvailableSlots();
    }
  }, [selectedDate, bookingData?.details?.gender, bookingData?.services]);

  const loadAvailableSlots = async () => {
    try {
      setLoading(true);
      setError('');

      console.log('🔍 loadAvailableSlots called for date:', selectedDate);
      console.log('📅 Selected date (raw):', selectedDate);
      console.log('👤 Client gender:', bookingData?.details?.gender);
      console.log('🛍️ Selected services (raw):', bookingData?.services);
      console.log('🛍️ Selected services (stringified):', JSON.stringify(bookingData?.services, null, 2));

      // Calculate total duration from selected services
      const selectedServices = bookingData?.services || [];
      console.log('📦 Selected services array:', selectedServices);
      console.log('📦 Number of services:', selectedServices?.length);

      let totalDuration = wellnessService.calculateTotalDuration(selectedServices);

      console.log(`📊 Total duration calculated: ${totalDuration} minutes`);

      // CRITICAL: If duration is 0, use default 60 minutes to prevent RPC errors
      if (!totalDuration || totalDuration === 0) {
        console.warn('⚠️ WARNING: Duration is 0! Services may not have duration_minutes field.');
        console.warn('⚠️ Using default duration of 60 minutes as fallback.');
        totalDuration = 60;
      }

      // For therapy bookings, get available therapists (excludes doctor consultation appointments)
      // We'll check availability for each potential time slot
      const testSlots = generateTimeSlots();
      console.log('Generated time slots:', testSlots);

      const now = new Date();
      const currentTime = now.getHours() * 60 + now.getMinutes(); // Current time in minutes
      const isToday = selectedDate?.toDateString() === now?.toDateString();

      const slotsWithAvailability = [];

      for (const slot of testSlots) {
        console.log(`Checking availability for slot: ${slot} (duration: ${totalDuration}min)`);

        // Parse slot time (format: "HH:MM")
        const [hours, minutes] = slot.split(':').map(Number);
        const slotTimeInMinutes = hours * 60 + minutes;

        // Determine if slot is in the past
        const isPast = isToday && slotTimeInMinutes < currentTime;

        let status = 'available';
        let staffCount = 0;

        if (isPast) {
          status = 'past';
        } else {
          // Format date as YYYY-MM-DD for RPC function
          const formattedDate = selectedDate?.toISOString()?.split('T')[0];

          // Format time as HH:MM:SS for PostgreSQL TIME type
          const formattedTime = slot.includes(':') && slot.split(':').length === 2
            ? `${slot}:00`  // Add seconds if not present
            : slot;

          console.log(`\n🔍 Checking slot ${slot}:`);
          console.log(`  📅 Formatted date: ${formattedDate}`);
          console.log(`  ⏰ Time (original): ${slot}`);
          console.log(`  ⏰ Time (formatted): ${formattedTime}`);
          console.log(`  👤 Gender: ${bookingData?.details?.gender}`);
          console.log(`  ⏱️ Duration: ${totalDuration} minutes`);
          console.log(`  👥 Therapists required: 1`);

          // Check therapist availability
          const availableStaff = await staffService?.getAvailableTherapistsForTherapy(
            formattedDate, // Pass date as YYYY-MM-DD string
            formattedTime, // Pass time as HH:MM:SS string
            bookingData?.details?.gender,
            totalDuration, // Pass dynamic duration
            1 // Always pass 1 - we only need at least 1 therapist available
          );

          console.log(`  📦 RPC Response (raw):`, availableStaff);
          console.log(`  📦 RPC Response (type):`, typeof availableStaff);
          console.log(`  📦 RPC Response (is array):`, Array.isArray(availableStaff));
          console.log(`  📦 RPC Response (stringified):`, JSON.stringify(availableStaff, null, 2));

          staffCount = availableStaff?.length || 0;

          // If no therapists available, mark as booked
          if (staffCount === 0) {
            status = 'booked';
            console.log(`  ❌ Result: BOOKED (0 therapists available)`);
          } else {
            status = 'available';
            console.log(`  ✅ Result: AVAILABLE (${staffCount} therapists available)`);
          }
        }

        slotsWithAvailability?.push({
          time: slot,
          status: status,
          available: status === 'available',
          staffCount: staffCount
        });
      }

      console.log('All slots with status:', slotsWithAvailability);
      setAvailableSlots(slotsWithAvailability);

      const availableCount = slotsWithAvailability?.filter(s => s?.status === 'available')?.length;
      const bookedCount = slotsWithAvailability?.filter(s => s?.status === 'booked')?.length;
      const pastCount = slotsWithAvailability?.filter(s => s?.status === 'past')?.length;

      console.log(`📊 Slot Summary - Available: ${availableCount}, Booked: ${bookedCount}, Past: ${pastCount}`);

      // Don't show error - always show all slots with their statuses
    } catch (err) {
      setError('Failed to check availability');
      console.error('❌ Error checking availability:', err);
      console.error('Error details:', err?.message, err?.stack);
    } finally {
      setLoading(false);
    }
  };

  // Check if a date is Monday (weekly off day)
  const isMonday = (date) => {
    return date?.getDay() === 1; // 0 = Sunday, 1 = Monday, 2 = Tuesday, etc.
  };

  // Check if a date is a weekly off (no available slots)
  const isWeeklyOff = (date) => {
    // Monday is always weekly off
    if (isMonday(date)) {
      return true;
    }
    // For other days, check if it's the selected date and has no available slots
    if (selectedDate?.toDateString() === date?.toDateString()) {
      return availableSlots?.length > 0 && availableSlots?.every(s => !s?.available);
    }
    return false;
  };

  const generateTimeSlots = () => {
    const slots = [];
    const startHour = 8; // 8 AM - Clinic opens at 8:00 AM
    const endHour = 19; // 7 PM - Clinic closes at 7:00 PM (19:00)
    const intervalMinutes = 30;

    for (let hour = startHour; hour < endHour; hour++) {
      for (let minute = 0; minute < 60; minute += intervalMinutes) {
        const timeString = `${hour?.toString()?.padStart(2, '0')}:${minute?.toString()?.padStart(2, '0')}`;
        slots?.push(timeString);
      }
    }

    return slots;
  };

  const handleDateSelect = (date) => {
    setSelectedDate(date);
    setSelectedSlot(null);
    onDataUpdate({
      dateTime: {
        date: date,
        timeSlot: null,
      },
    });
  };

  const handleTimeSelect = (time) => {
    setSelectedSlot(time);
    onDataUpdate({
      dateTime: {
        date: selectedDate,
        timeSlot: time,
      },
    });
  };

  const formatDate = (date) => {
    return date?.toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
    });
  };

  const formatTimeSlot = (time) => {
    const [hour, minute] = time?.split(':');
    const hourNum = parseInt(hour, 10);
    const period = hourNum >= 12 ? 'PM' : 'AM';
    const hour12 = hourNum % 12 || 12;
    return `${hour12}:${minute} ${period}`;
  };

  const getDayName = (date) => {
    return date?.toLocaleDateString("en-US", { weekday: "short" });
  };

  const isToday = (date) => {
    const today = new Date();
    return date?.toDateString() === today?.toDateString();
  };

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center gap-2">
        <CalendarIcon className="w-6 h-6 text-purple-600" />
        Select Date & Time
      </h2>
      {/* Date Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">
          Choose a Date
        </label>
        <div className="grid grid-cols-7 gap-2">
          {availableDates?.map((date, index) => {
            const isSelected = selectedDate?.toDateString() === date?.toDateString();
            const isMondayDate = isMonday(date);
            const isOff = isWeeklyOff(date);

            return (
              <button
                key={index}
                onClick={() => !isMondayDate && handleDateSelect(date)}
                disabled={isMondayDate}
                className={`p-3 rounded-lg text-center transition-all relative ${isMondayDate
                  ? "bg-gray-200 text-gray-400 cursor-not-allowed opacity-50"
                  : isSelected
                    ? "bg-purple-600 text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
              >
                <div className="text-xs font-medium">
                  {getDayName(date)}
                </div>
                <div className="text-lg font-bold">{date?.getDate()}</div>
                {isMondayDate && (
                  <div className="text-xs mt-1 bg-red-500 text-white px-1 py-0.5 rounded">
                    Weekly Off
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>
      {/* Time Slot Selection with availability */}
      {selectedDate && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">
            <Clock className="w-4 h-4 inline mr-2" />
            Time Slots for {formatDate(selectedDate)}
          </label>

          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
            </div>
          ) : error ? (
            <div className="text-center py-8 bg-red-50 rounded-lg border border-red-200">
              <p className="text-red-600">{error}</p>
              <p className="text-sm text-red-500 mt-2">Please try again</p>
            </div>
          ) : (
            <>
              {/* Slot Legend */}
              <div className="flex flex-wrap gap-4 mb-4 text-xs">
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded border-2 border-green-500 bg-green-50"></div>
                  <span className="text-gray-600">Available</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded border-2 border-red-500 bg-red-50"></div>
                  <span className="text-gray-600">Booked</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded border-2 border-gray-300 bg-gray-100"></div>
                  <span className="text-gray-600">Past</span>
                </div>
              </div>

              {/* Time Slots Grid */}
              <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
                {availableSlots?.map((slot, index) => {
                  const isSelected = selectedSlot === slot?.time;
                  const isAvailable = slot?.status === 'available';
                  const isBooked = slot?.status === 'booked';
                  const isPast = slot?.status === 'past';

                  return (
                    <button
                      key={index}
                      onClick={() => isAvailable && handleTimeSelect(slot?.time)}
                      disabled={!isAvailable}
                      className={`p-3 rounded-lg border-2 transition-all ${isSelected
                        ? 'border-purple-600 bg-purple-50 text-purple-600 ring-2 ring-purple-200'
                        : isAvailable
                          ? 'border-green-500 bg-green-50 text-green-700 hover:border-green-600 hover:bg-green-100'
                          : isBooked
                            ? 'border-red-500 bg-red-50 text-red-700 cursor-not-allowed'
                            : 'border-gray-300 bg-gray-100 text-gray-400 cursor-not-allowed'
                        }`}
                      title={
                        isPast
                          ? 'This time has passed'
                          : isBooked
                            ? 'No therapists available for this time'
                            : `${slot?.staffCount} therapist(s) available`
                      }
                    >
                      <div className="text-sm font-semibold">
                        {formatTimeSlot(slot?.time)}
                      </div>
                      <div className="text-xs mt-1">
                        {isPast ? 'Past' : isBooked ? 'Booked' : 'Available'}
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Summary */}
              {availableSlots?.length > 0 && (
                <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-sm text-blue-800">
                    <span className="font-semibold">
                      {availableSlots?.filter(s => s?.status === 'available')?.length}
                    </span>{' '}
                    available slot(s) •{' '}
                    <span className="font-semibold">
                      {availableSlots?.filter(s => s?.status === 'booked')?.length}
                    </span>{' '}
                    booked •{' '}
                    <span className="font-semibold">
                      {availableSlots?.filter(s => s?.status === 'past')?.length}
                    </span>{' '}
                    past
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default DateTimeSelector;